import type { TrainingAnswers, TrainingPlayerPage } from '@hcc/types';
import { pageComplete, pageQuestionCount } from './answerValues';

/**
 * The page-chip strip: one dot per page, filled when answered (or a slide),
 * ringed when current. Tapping jumps — order of pages never matters for
 * grading, only completeness. A question group is one chip carrying several
 * questions, so its chip is numbered with the range it covers.
 */
export function SlideNavigator({
  pages,
  step,
  answers,
  correctIds,
  onJump,
}: {
  pages: TrainingPlayerPage[];
  step: number;
  answers: TrainingAnswers;
  /** try_again mode: questions already verified correct. */
  correctIds: ReadonlySet<string>;
  onJump: (index: number) => void;
}) {
  let questionNo = 0;
  return (
    <ol className="flex flex-wrap items-center gap-1.5" aria-label="Pages">
      {pages.map((p, i) => {
        const count = pageQuestionCount(p);
        const isQuestion = count > 0;
        const first = questionNo + 1;
        questionNo += count;
        const chip = !isQuestion ? '·' : count === 1 ? `${first}` : `${first}-${questionNo}`;
        const label = isQuestion
          ? count === 1
            ? `Question ${first}`
            : `Questions ${first} to ${questionNo}`
          : `Page ${i + 1}`;
        const done = isQuestion
          ? correctIds.has(p.id) || pageComplete(p, answers)
          : true;
        const current = i === step;
        return (
          <li key={p.id}>
            <button
              type="button"
              aria-label={label}
              aria-current={current ? 'step' : undefined}
              onClick={() => onJump(i)}
              className={`flex h-7 min-w-7 items-center justify-center rounded-full border px-1.5 text-xs font-semibold tabular-nums transition-colors ${
                current
                  ? 'border-accent bg-accent text-fg-on-accent'
                  : done && isQuestion
                    ? 'border-success-soft/40 bg-success-soft/15 text-success-soft-fg'
                    : 'border-line/10 bg-surface-2 text-fg-muted hover:bg-surface'
              }`}
            >
              {chip}
            </button>
          </li>
        );
      })}
    </ol>
  );
}
